import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  class: "mt-4",
  style: {"width":"100%","overflow":"auto"}
}

import moment from 'moment';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { setCurrentPageTitle } from '@/core/helpers/breadcrumb';
import { swalErrNotification } from '@/core/helpers/utils';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { useCreatingForm } from '@/core/hooks/use-creating-form';
import { useDateTime } from '@/core/hooks/use-date-time-hook';
import { useLoading } from '@/core/hooks/use-loading';
import { BaoCaoService } from '@/core/services/BaoCao.service';
import { KhoService } from '@/core/services/Kho.service';
import { IHoaDonNhapKhoResponseBase, IKhoResponseBase, ITaiKhoanResponseBase } from '@/core/interfaces/ApiResponses';
import BangKeChiTietNhapPrintModal from './print-modals/BangKeChiTietNhapPrintModal.vue';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import ChiTietNhapTable from './table-components/ChiTietNhapTable.vue';
import DivCol from '@/layout/_shared/DivCol.vue';
import DivRow from '@/layout/_shared/DivRow.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import PageRowLayout from '@/layout/_shared/PageRowLayout.vue';
import ResponsiveLayout from '@/layout/_shared/ResponsiveLayout.vue';
import TaiKhoanSelectionV2 from '@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BangKeChiTietNhap',
  setup(__props) {

const { disableDate, defaultDate } = useDateTime();

setCurrentPageTitle('Báo cáo');

const { startLoading, endLoading } = useLoading();

const { formRef } = useCreatingForm();

const { ButtonsType, ButtonTypeColors } = useButtonCustom();

const store = useStore();

const currentYear = computed(() => store.getters.getSelectedYear);

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  khoId: -1,
});

const taiKhoanData = ref<{
  id: number;
  maTaiKhoan: string;
  maChiTiet?: string;
  tenTaiKhoan: string;
}>({
  id: 0,
  maChiTiet: '',
  maTaiKhoan: '',
  tenTaiKhoan: '',
});

const fromDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error('Bắt buộc phải nhập'));
  }

  if (value && form.value.to && moment(value).isAfter(form.value.to)) {
    callback(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
  }

  callback();
}

const toDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error('Bắt buộc phải nhập'));
  }

  if (value && form.value.from && moment(value).isBefore(form.value.from)) {
    callback(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
  }

  callback();
}

const khoItems = ref<IKhoResponseBase[]>([]);

const fetchKhoItems = async () => {
  startLoading();

  try {
    const { data: { data: { data } } } = await KhoService.fetchAllWarehouses();

    khoItems.value = data;

    endLoading();
  } catch (e) {
    endLoading();

    swalErrNotification(e, 'Có lỗi khi lấy danh sách kho');
  }
}

onMounted(() => {
  nextTick(async () => {
    await fetchKhoItems();
  });
});

const isFiltersChanging = ref(true);

const rules = ref({
  from: [
    {
      validator: fromDateValidation,
      trigger: 'change',
    },
  ],
  to: [
    {
      validator: toDateValidation,
      trigger: 'change',
    },
  ],
  khoId: [
    {
      type: 'number',
      message: "Phải chọn kho",
      trigger: 'change',
      min : 1,
    }
  ],
});

const selectingKho = computed(() => {
  if (!form.value.khoId) return null;

  return khoItems.value.find(({ id }) => id === form.value.khoId);
});

const onSelectTaiKhoan  = ({
  taiKhoanItem: {
  id: id_tai_khoan,
  ma_tai_khoan,
  ma_chi_tiet,
  ten_tai_khoan,
  }
}: {
  taiKhoanItem: ITaiKhoanResponseBase,
}) => {
  taiKhoanData.value.maTaiKhoan = ma_tai_khoan;
  taiKhoanData.value.tenTaiKhoan = ten_tai_khoan;
  taiKhoanData.value.id = id_tai_khoan;

  if (ma_chi_tiet) {
    taiKhoanData.value.maChiTiet = ma_chi_tiet.toString();
  }
}

watch(
  form,
  () => {
    isFiltersChanging.value = true;
  },
  {
    deep: true,
    immediate: true,
  }
);

const hoaDonNhapKhoItems = ref<IHoaDonNhapKhoResponseBase[]>([]);

const generateBaoCao = () => {
  if (!formRef.value) return;

  if (!taiKhoanData.value.maChiTiet) {
    swalErrNotification(
      null,
      'Bắt buộc phải chọn mã chi tiết',
    );
  }

  formRef.value.validate(async (isValid) => {
    if (isValid) {
      try {
        await startLoading();

        const { data: { data } } = await BaoCaoService.getBangKeChiTietNhap({
          from: form.value.from.getMonth() + 1,
          to: form.value.to.getMonth() + 1,
          idKho: selectingKho.value?.id ?? 0,
          mact: taiKhoanData.value.maChiTiet ?? '',
          matk: taiKhoanData.value.maTaiKhoan,
        });

        await endLoading();

        hoaDonNhapKhoItems.value = data;

        isFiltersChanging.value = false;
      } catch (e) {
        endLoading();

        swalErrNotification(e, 'Có lỗi xảy ra');
      }
    } else return false;
  });
}

const bangKeChiTietNhapPrintModalRef = ref();

const showPrintButton = computed(() => {
  if (isFiltersChanging.value) return false;

  if (!hoaDonNhapKhoItems.value.length) return false;

  if (!selectingKho.value || !form.value.from || !form.value.to || !taiKhoanData.value.maChiTiet) return false;

  return true;
});

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BangKeChiTietNhapPrintModal, {
      ref_key: "bangKeChiTietNhapPrintModalRef",
      ref: bangKeChiTietNhapPrintModalRef,
      from: new Date(form.value.from) ?? null,
      to: new Date(form.value.to) ?? null,
      items: hoaDonNhapKhoItems.value,
      kho: selectingKho.value ?? null,
      "tai-khoan-data": taiKhoanData.value,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (bangKeChiTietNhapPrintModalRef.value.isOpen = false))
    }, null, 8, ["from", "to", "items", "kho", "tai-khoan-data"]),
    _createVNode(PageRowLayout, null, {
      "page-row-header": _withCtx(() => [
        _createTextVNode(" Bảng kê chi tiết nhập ")
      ]),
      "page-row-content": _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "formRef",
          ref: formRef,
          model: form.value,
          rules: rules.value,
          onSubmit: _withModifiers(generateBaoCao, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(ResponsiveLayout, null, {
              "col-1": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Kho: ")
                  ]),
                  _: 1
                })
              ]),
              "col-2": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "khoId",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: form.value.khoId,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.khoId) = $event)),
                      class: "w-100",
                      name: "khoId"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "Chưa chọn",
                          value: -1
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(khoItems.value, (kho) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: kho.id,
                            label: kho.ten_kho,
                            value: kho.id
                          }, null, 8, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(ResponsiveLayout, null, {
              "col-1": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Từ tháng:")
                  ]),
                  _: 1
                })
              ]),
              "col-2": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "from",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_date_picker, {
                      modelValue: form.value.from,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.from) = $event)),
                      class: "w-100",
                      "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                      type: "month",
                      placeholder: "Từ",
                      name: "from",
                      format: "MM/YYYY"
                    }, null, 8, ["modelValue", "disabled-date"])
                  ]),
                  _: 1
                })
              ]),
              "col-3": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Đến tháng: ")
                  ]),
                  _: 1
                })
              ]),
              "col-4": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "to",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_date_picker, {
                      modelValue: form.value.to,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.to) = $event)),
                      class: "w-100",
                      "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                      placeholder: "Đến",
                      name: "to",
                      format: "MM/YYYY",
                      type: "month"
                    }, null, 8, ["modelValue", "disabled-date"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(TaiKhoanSelectionV2, {
              label: "Tài Khoản:",
              "label-classes": "text-muted",
              "label-cols": 2,
              "is-bolder": false,
              "ma-tai-khoan-prop": taiKhoanData.value.maTaiKhoan ? parseInt(taiKhoanData.value.maTaiKhoan) : undefined,
              "ma-chi-tiet-prop": taiKhoanData.value.maChiTiet ? taiKhoanData.value.maChiTiet : undefined,
              onOnSelect: onSelectTaiKhoan
            }, null, 8, ["ma-tai-khoan-prop", "ma-chi-tiet-prop"]),
            (taiKhoanData.value.tenTaiKhoan)
              ? (_openBlock(), _createBlock(DivRow, {
                  key: 0,
                  class: "mt-5"
                }, {
                  default: _withCtx(() => [
                    _createVNode(DivCol, { cols: 2 }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, { "is-text-muted": true }, {
                          default: _withCtx(() => [
                            _createTextVNode("Đối tác:")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(DivCol, { cols: 3 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          modelValue: taiKhoanData.value.tenTaiKhoan,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((taiKhoanData.value.tenTaiKhoan) = $event)),
                          disabled: "",
                          readonly: "",
                          rows: 3,
                          type: "textarea"
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(DivRow, null, {
              default: _withCtx(() => [
                _createVNode(DivCol, { cols: 8 }),
                _createVNode(DivCol, {
                  cols: 4,
                  class: "d-flex justify-content-end"
                }, {
                  default: _withCtx(() => [
                    (showPrintButton.value)
                      ? (_openBlock(), _createBlock(ButtonCustom, {
                          key: 0,
                          "button-type": _unref(ButtonsType).FA_PRINT,
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (bangKeChiTietNhapPrintModalRef.value.isOpen = true))
                        }, {
                          "button-label": _withCtx(() => [
                            _createTextVNode("In phiếu")
                          ]),
                          _: 1
                        }, 8, ["button-type"]))
                      : _createCommentVNode("", true),
                    _createVNode(ButtonCustom, {
                      "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                      "color-type": _unref(ButtonTypeColors).SUCCESS,
                      onClick: generateBaoCao
                    }, null, 8, ["button-type", "color-type"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model", "rules"]),
        _createElementVNode("div", _hoisted_1, [
          (!isFiltersChanging.value)
            ? (_openBlock(), _createBlock(ChiTietNhapTable, {
                key: 0,
                items: hoaDonNhapKhoItems.value
              }, null, 8, ["items"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    })
  ], 64))
}
}

})